import { useEffect, useRef, useState } from "react";
import { supabase } from "../../lib/supabase";
import { COLORS } from "../../lib/options";
import {
  Button, MoneyField, Select, ErrorNote, Confirm, Panel, OptionSelect,
} from "../../components/ui";
import { formatMoney, parseMoney } from "../../lib/money";
import { bpsToPercent, surtaxLabel } from "../../lib/counties";
import { WARRANTY_TERMS, termFor } from "../../lib/warranty";
import { buildPacketForDeal, buildReceiptForDeal, openForPrint, packetFor,
         DEPOSIT_PACKET, PACKET_LABELS } from "./buildPacket";
import { balanceIsOwed } from "../../lib/dealStatus";
import { loadCounties, type County } from "../../lib/counties";
import { cardPaymentsConfigured, refundPayment, CARD_MAX_CENTS } from "../../lib/stripe";
import { InsuranceCodeField, OdometerField, PastDateField, TextField } from "../../components/fields";
import CardPayment from "./CardPayment";
import WeOwePanel from "./WeOwePanel";
import BalanceDue from "./BalanceDue";
import { markKeptIfSettled } from "./promises";
import SignPacket from "./SignPacket";
import SignChoice from "./SignChoice";
import type { SigningSession } from "./signing";
import EmailPacket from "./EmailPacket";
import {
  saveDealEdits, savePaymentEdits, reversePayment, unreversePayment,
  deleteBlockedReason, deleteDeal, type DealEdits,
} from "./dealEdits";
import {
  archiveDealDocument, listDealDocuments, fetchArchivedDocument,
  labelForDocType, filenameFor, formatSize, type ArchivedDoc,
} from "../../lib/documents";
import { SaveBar, SaveError, SaveStatus } from "../../components/save";
import {
  describeSaveError, mustSave, SaveFailure, useSaveState,
} from "../../lib/saving";

interface Deal {
  id: string; deal_no: string; agreement_date: string; status: string;
  county_name: string; county_surtax_bps: number;
  co_buyer_id: string | null; joint_ownership: string | null;
  cash_price_cents: number; predelivery_service_cents: number;
  electronic_filing_cents: number; license_registration_cents: number;
  title_fee_cents: number; temp_tag_cents: number;
  trade_allowance_cents: number; trade_payoff_cents: number;
  cash_down_cents: number; deferred_down_cents: number;
  taxable_amount_cents: number; state_tax_cents: number; surtax_cents: number;
  total_sales_price_cents: number; total_amount_due_cents: number;
  unpaid_balance_cents: number;
  void_reason: string | null;
  warranty_days: number; warranty_miles: number;
  insurance_company: string | null; insurance_policy_no: string | null;
  insurance_company_code: string | null;
  buyer: {
    id: string; first_name: string; middle_name: string | null;
    last_name: string; suffix: string | null; date_of_birth: string;
    id_type: string; id_number: string; id_state: string;
    id_expiration: string | null;
    street: string; city: string; state: string; zip: string;
    phone_cell: string | null; email: string | null;
  } | null;
  co_buyer: { id: string; first_name: string; last_name: string } | null;
  vehicle: { id: string; year: number; make: string; model: string;
             stock_no: string; vin: string } | null;
}

interface Payment {
  id: string; paid_on: string; amount_cents: number;
  method: string; reference_no: string | null;
  reversed_at: string | null; reversed_reason: string | null;
  /* Written by the payments worker after Stripe confirms. Never a card
     number — only what is safe to keep and enough to find the charge again. */
  card_brand: string | null;
  card_last4: string | null;
  stripe_payment_intent_id: string | null;
  stripe_refund_id: string | null;
}

/** "Visa" rather than "visa"; leaves anything unexpected alone. */
const CARD_BRAND: Record<string, string> = {
  visa: "Visa", mastercard: "Mastercard", amex: "American Express",
  discover: "Discover", diners: "Diners Club", jcb: "JCB", unionpay: "UnionPay",
};

/** How a card payment reads in the list: Visa ···· 4242 */
function describeCard(p: Payment): string | null {
  if (!p.card_last4) return null;
  const brand = p.card_brand ? (CARD_BRAND[p.card_brand] ?? p.card_brand) : "Card";
  return `${brand} \u00b7\u00b7\u00b7\u00b7 ${p.card_last4}`;
}

const METHODS = ["cash", "check", "card", "ach", "wire", "lender_check", "other"];
const ID_LABEL: Record<string, string> = {
  drivers_license: "Driver license",
  state_id: "State ID",
  passport: "Passport",
  military_id: "Military ID",
};

const METHOD_LABEL: Record<string, string> = {
  cash: "Cash", check: "Check", card: "Card", ach: "ACH",
  wire: "Wire", lender_check: "Lender check", other: "Other",
};

export default function DealDetail({ id, onBack, onOpenCustomer }: {
  id: string; onBack: () => void;
  /** Opens the customer record, where their details are edited. Kept in one
      place rather than duplicated onto this screen: a license number typed
      twice is a license number that can disagree with itself. */
  onOpenCustomer: (customerId: string) => void;
}) {
  const [d, setD] = useState<Deal | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);
  const [signing, setSigning] = useState(false);
  /* A remote session the customer has already signed, being carried through to
     the dealership signature. Distinct from `signing`, which starts fresh. */
  const [resuming, setResuming] = useState<SigningSession | null>(null);
  const [dealerName, setDealerName] = useState("the dealership");
  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [docs, setDocs] = useState<ArchivedDoc[]>([]);
  const [archiveNote, setArchiveNote] = useState<string | null>(null);
  const [reprinting, setReprinting] = useState<string | null>(null);
  const [counties, setCounties] = useState<County[]>([]);
  const [edit, setEdit] = useState<DealEdits | null>(null);
  /* The car's own details, corrected from here because the paperwork is
     printed from here. Mileage especially: a reading taken at purchase is
     often a few miles short of the car the buyer drives away. */
  const [vEdit, setVEdit] = useState<{ odometer: string; color: string; trim: string } | null>(null);
  const [editPay, setEditPay] = useState<
    { id: string; paid_on: string; amount: string; method: string; ref: string } | null>(null);
  const [reversing, setReversing] = useState<string | null>(null);
  /* Removing a payment erases the record that money was taken. Reversing is
     usually what is wanted; either way it gets a confirmation. */
  const [confirmPayment, setConfirmPayment] = useState<Payment | null>(null);
  const [takingCard, setTakingCard] = useState(false);
  /* Which confirmation id was just copied, so the link can say so. */
  const [copied, setCopied] = useState<string | null>(null);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(null), 1600);
    return () => clearTimeout(t);
  }, [copied]);
  const [reverseReason, setReverseReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadCounties().then(setCounties).catch(() =>
      setErr("Could not load the county list. Tax rates cannot be chosen "
        + "until it loads — check your connection and reopen this page.")); }, []);

  /* Everyone on file, for changing who the deal is for. */
  const [people, setPeople] = useState<
    { id: string; first_name: string; last_name: string }[]>([]);
  useEffect(() => {
    supabase.from("customers").select("id,first_name,last_name")
      .order("last_name").order("first_name")
      .then(({ data }) => setPeople(data ?? []));
  }, []);

  const [pay, setPay] = useState({ amount: "", method: "cash", ref: "" });

  /* One save state per thing that can be saved, so each says "Saving…",
     "Saved ✓ 3:04 PM" or why not beside its own button — this page is long,
     and a single error line at the bottom was a screen away from the Save
     that caused it. */
  const editStart = useRef<string>("");
  const editPayStart = useRef<string>("");
  const dealDirty = edit !== null && JSON.stringify({ edit, vEdit }) !== editStart.current;
  const dealSave = useSaveState({ dirty: dealDirty, what: "the deal changes" });
  const payEditDirty = editPay !== null && JSON.stringify(editPay) !== editPayStart.current;
  const payEditSave = useSaveState({ dirty: payEditDirty, what: "the payment changes" });
  const payDirty = Boolean(pay.amount.trim() || pay.ref.trim());
  const paySave = useSaveState({ dirty: payDirty, what: "the payment you were recording" });
  /* Instant actions on a payment row: remove, reverse, refund, undo. */
  const payAct = useSaveState();
  const [payActLabel, setPayActLabel] = useState("Saved");
  /* Instant actions on the deal: status, void, delete. */
  const dealAct = useSaveState();
  const [dealActLabel, setDealActLabel] = useState("Saved");
  const busy = dealSave.saving || payEditSave.saving || paySave.saving
    || payAct.saving || dealAct.saving;

  useEffect(() => {
    supabase.from("dealer_settings").select("legal_name,dba").maybeSingle()
      .then(({ data }) => { if (data) setDealerName(data.dba ?? data.legal_name); });
  }, []);

  async function load() {
    const [{ data, error }, { data: pd }] = await Promise.all([
      supabase.from("deals").select(`*,
        buyer:customers!deals_buyer_id_fkey(
          id,first_name,middle_name,last_name,suffix,date_of_birth,
          id_type,id_number,id_state,id_expiration,
          street,city,state,zip,phone_cell,email),
        co_buyer:customers!deals_co_buyer_id_fkey(id,first_name,last_name),
        vehicle:vehicles!deals_vehicle_id_fkey(id,year,make,model,stock_no,vin,odometer,exterior_color,trim_level)`)
        .eq("id", id).single(),
      supabase.from("payments").select("*").eq("deal_id", id).order("paid_on"),
    ]);
    if (error) { setErr("Could not load this deal."); return; }
    setD(data as unknown as Deal);
    setPayments((pd ?? []) as Payment[]);
    loadDocs();
  }

  /** The archive is a side panel on this screen. If it cannot be read the
      deal itself still has to open, so this reports rather than throwing. */
  async function loadDocs() {
    try {
      setDocs(await listDealDocuments(id));
    } catch (e) {
      setArchiveNote(e instanceof Error ? e.message : "Could not read the archive.");
    }
  }
  useEffect(() => { load(); }, [id]);

  // Reversed money is not collected money. The deal_balances view applies the
  // same rule, so the screen and the receipt cannot disagree.
  const live = payments.filter((p) => !p.reversed_at);
  const paid = live.reduce((n, p) => n + p.amount_cents, 0);

  /** The car follows the deal. Its failure used to be ignored, which left a
      sold car showing as available on the lot with nothing said. */
  async function syncVehicle(vs: string, dealNow: string) {
    if (!d?.vehicle) return;
    const res = await supabase.from("vehicles")
      .update({ status: vs }).eq("id", d.vehicle.id).select("id");
    if (res.error || !res.data?.length) {
      throw new SaveFailure(
        `The deal is now ${dealNow}, but the car's status on the lot did not change`
        + (res.error ? ` (${describeSaveError(res.error)})` : "")
        + ". Change it on the vehicle page.");
    }
  }

  async function setStatus(status: string) {
    setErr(null);
    setDealActLabel(`Deal marked ${status}`);
    // Moving a voided deal back to a live status un-voids it, so the reason
    // for voiding goes with it rather than lingering as a stale note.
    const patch: Record<string, unknown> = { status };
    if (d?.status === "voided") patch.void_reason = null;
    await dealAct.run(async () => {
      mustSave(await supabase.from("deals").update(patch).eq("id", id).select("id"));
      // Selling the deal takes the car off the lot; unwinding puts it back.
      const vs = status === "sold" ? "sold"
        : status === "deposit" ? "on_hold" : "available";
      try { await syncVehicle(vs, status); }
      finally { void load(); }
    }, { toast: `Deal marked ${status}` });
  }

  async function addPayment() {
    const cents = parseMoney(pay.amount);
    if (!cents || cents <= 0) { paySave.fail("Enter an amount greater than zero."); return; }
    const sent = pay;
    const ok = await paySave.run(async () => {
      mustSave(await supabase.from("payments").insert({
        deal_id: id, amount_cents: cents, method: sent.method,
        reference_no: sent.ref.trim() || null,
      }));
    }, {
      toast: `${formatMoney(cents)} by ${METHOD_LABEL[sent.method] ?? sent.method} recorded`,
    });
    if (!ok) return;            // the amount stays in the box
    setPay({ amount: "", method: "cash", ref: "" });
    /* If that cleared the balance, the promise to pay it is kept. Best effort:
       the money is already recorded and must not look like it failed. */
    await markKeptIfSettled(id);
    void load();
  }

  async function removePayment(pid: string) {
    setPayActLabel("Payment removed");
    // This used to discard the error, so a refused delete looked like a
    // successful one until the page was reloaded.
    const ok = await payAct.run(async () => {
      mustSave(await supabase.from("payments").delete().eq("id", pid).select("id"));
    }, { toast: "Payment removed" });
    setConfirmPayment(null);
    if (ok) void load();
  }

  async function doVoid() {
    setErr(null);
    setDealActLabel("Deal voided");
    const ok = await dealAct.run(async () => {
      mustSave(await supabase.from("deals")
        .update({ status: "voided", void_reason: voidReason.trim() || "No reason given" })
        .eq("id", id).select("id"));
      try { await syncVehicle("available", "voided"); }
      finally { void load(); }
    }, { toast: "Deal voided" });
    if (ok) setVoiding(false);
  }

  /**
   * File what was just printed, if this deal is one whose paperwork matters.
   * The document is already on screen by now, so a failure here is a warning
   * and not an error — but it is always shown. A document that quietly failed
   * to file is the kind of thing you find out about years later.
   */
  async function fileIfCommitted(bytes: Uint8Array, docType: string) {
    if (!d || !balanceIsOwed(d.status)) return;
    try {
      await archiveDealDocument(bytes, {
        dealId: id, docType, dealStatus: d.status,
      });
      setArchiveNote(null);
      await loadDocs();
    } catch (e) {
      setArchiveNote(
        (e instanceof Error ? e.message : "Could not file this document.")
          + " It printed, but no copy was kept.",
      );
    }
  }

  /* ---------------- editing the deal ---------------- */

  /* The edit form sits at the top of the page and the panels that open it sit
     a screen and a half below. Clicking "Edit insurance" therefore opened a
     form the person could not see, which reads exactly like a deal that
     refuses to be edited — the first thing it was reported as. These two
     carry the person to the fields they asked for. */
  const editTop = useRef<HTMLHeadingElement>(null);
  const insuranceFields = useRef<HTMLDivElement>(null);
  const warrantyField = useRef<HTMLDivElement>(null);
  const [goTo, setGoTo] = useState<"top" | "insurance" | "warranty" | null>(null);

  useEffect(() => {
    if (!edit || !goTo) return;
    const target = goTo === "insurance" ? insuranceFields.current
                 : goTo === "warranty" ? warrantyField.current
                 : editTop.current;
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
    /* Focus the field as well as scrolling, so a keyboard or a phone lands in
       the box rather than next to it. */
    if (goTo === "insurance") {
      insuranceFields.current?.querySelector("input")?.focus({ preventScroll: true });
    }
    if (goTo === "warranty") {
      warrantyField.current?.querySelector("select")?.focus({ preventScroll: true });
    }
    setGoTo(null);
  }, [edit, goTo]);

  function startEdit(where: "top" | "insurance" | "warranty" = "top") {
    if (!d) return;
    setErr(null);
    setGoTo(where);
    // Already open: this is a request to go there, not to start again, and
    // starting again would throw away whatever has been typed.
    if (edit) return;
    dealSave.reset();
    const startV = d.vehicle ? {
      odometer: String((d.vehicle as { odometer?: number }).odometer ?? ""),
      color: (d.vehicle as { exterior_color?: string }).exterior_color ?? "",
      trim: (d.vehicle as { trim_level?: string | null }).trim_level ?? "",
    } : null;
    const initial: DealEdits = {
      buyer_id: d.buyer?.id ?? "",
      co_buyer_id: d.co_buyer_id ?? null,
      joint_ownership: (d.joint_ownership as "and" | "or" | null) ?? null,
      agreement_date: d.agreement_date,
      county_name: d.county_name,
      county_surtax_bps: d.county_surtax_bps,
      cash_price_cents: d.cash_price_cents,
      predelivery_service_cents: d.predelivery_service_cents,
      electronic_filing_cents: d.electronic_filing_cents,
      license_registration_cents: d.license_registration_cents,
      title_fee_cents: d.title_fee_cents,
      temp_tag_cents: d.temp_tag_cents,
      trade_allowance_cents: d.trade_allowance_cents,
      trade_payoff_cents: d.trade_payoff_cents,
      cash_down_cents: d.cash_down_cents,
      deferred_down_cents: d.deferred_down_cents,
      warranty_days: d.warranty_days,
      warranty_miles: d.warranty_miles,
      insurance_company: d.insurance_company,
      insurance_policy_no: d.insurance_policy_no,
      insurance_company_code: d.insurance_company_code,
    };
    editStart.current = JSON.stringify({ edit: initial, vEdit: startV });
    setVEdit(startV);
    setEdit(initial);
  }

  function cancelEdit() {
    if (dealDirty && !window.confirm("Discard your changes to this deal?")) return;
    setEdit(null); setVEdit(null); dealSave.dismiss();
  }

  /* Raising a reading is ordinary — the car was driven between purchase and
     sale. Lowering one is either a typo being fixed or an odometer being
     rolled back, and the second is a federal offence. Allowed, because typos
     happen, but not silently. */
  const recordedMiles = Number((d?.vehicle as { odometer?: number } | undefined)?.odometer ?? 0);
  const newMiles = Number((vEdit?.odometer ?? "").replace(/\D/g, "")) || 0;
  const lowerMileage = Boolean(vEdit) && newMiles > 0 && newMiles < recordedMiles;
  const lowerMileageMsg = `Lower than the ${recordedMiles.toLocaleString("en-US")} `
    + "recorded. Only correct it down if the original was mistyped.";

  const editMoney = (k: keyof DealEdits) => (v: string) =>
    setEdit((e) => (e ? { ...e, [k]: parseMoney(v) ?? 0 } : e));

  async function commitEdit() {
    if (!edit) return;
    setErr(null);
    // Checked before anything is written, so a missing mileage cannot leave
    // the deal saved and the car not.
    const odo = vEdit ? Number(vEdit.odometer.replace(/\D/g, "")) : 0;
    if (vEdit && d?.vehicle && (!Number.isFinite(odo) || odo <= 0)) {
      dealSave.fail("Enter the mileage.");
      return;
    }
    const sentEdit = edit;
    const sentV = vEdit;
    const ok = await dealSave.run(async () => {
      await saveDealEdits(id, sentEdit);
      if (sentV && d?.vehicle) {
        const res = await supabase.from("vehicles").update({
          odometer: odo,
          exterior_color: sentV.color.trim() || null,
          trim_level: sentV.trim.trim() || null,
        }).eq("id", d.vehicle.id).select("id");
        if (res.error || !res.data?.length) {
          throw new SaveFailure(
            "The deal saved, but the car's mileage, color and trim did not"
            + (res.error ? ` (${describeSaveError(res.error)})` : "") + ".");
        }
      }
    }, { toast: "Deal changes saved" });
    if (!ok) return;            // the form stays open with every change in it
    setEdit(null);
    setVEdit(null);
    await load();
  }

  /* ---------------- payments ---------------- */

  async function commitPaymentEdit() {
    if (!editPay) return;
    const cents = parseMoney(editPay.amount);
    if (!cents || cents <= 0) { payEditSave.fail("Enter an amount greater than zero."); return; }
    const sent = editPay;
    const ok = await payEditSave.run(() => savePaymentEdits(sent.id, {
      paid_on: sent.paid_on,
      amount_cents: cents,
      method: sent.method,
      reference_no: sent.ref.trim() || null,
    }), { toast: "Payment changes saved" });
    if (!ok) return;
    setEditPay(null);
    await load();
  }

  function cancelPaymentEdit() {
    if (payEditDirty && !window.confirm("Discard your changes to this payment?")) return;
    setEditPay(null); payEditSave.dismiss();
  }

  async function commitReversal() {
    if (!reversing) return;
    const p = payments.find((x) => x.id === reversing);
    const isCard = p?.method === "card" && Boolean(p.stripe_payment_intent_id);
    setPayActLabel(isCard ? "Refunded and reversed" : "Payment reversed");
    const ok = await payAct.run(async () => {
      /* A card that was actually charged has to be refunded, not merely
         marked. Marking it alone leaves the deal saying the customer owes
         money the dealer is still holding. */
      if (isCard && p) {
        await refundPayment(p.id, reverseReason);
      } else {
        await reversePayment(reversing, reverseReason);
      }
    }, {
      toast: isCard ? "Refunded and reversed" : "Payment reversed",
      // A refund goes through Stripe; give it time rather than call a slow
      // refund a failure and invite a second one.
      timeoutMs: isCard ? 120_000 : undefined,
    });
    if (!ok) return;            // the reason stays typed in
    setReversing(null); setReverseReason("");
    await load();
  }

  /** Refund a card that was marked reversed before refunds existed. */
  async function refundOutstanding(p: Payment) {
    setPayActLabel("Refunded to the card");
    const ok = await payAct.run(() => refundPayment(p.id, "Refunded to the card"),
      { toast: "Refunded to the card", timeoutMs: 120_000 });
    if (ok) await load();
  }

  async function undoReversal(pid: string) {
    setPayActLabel("Reversal undone");
    const ok = await payAct.run(() => unreversePayment(pid), { toast: "Reversal undone" });
    if (ok) await load();
  }

  /* ---------------- deleting ---------------- */

  async function commitDelete() {
    setDealActLabel("Deal deleted");
    const ok = await dealAct.run(() => deleteDeal(id), { toast: `${d?.deal_no ?? "Deal"} deleted` });
    if (ok) onBack();
    else setDeleting(false);
  }

  async function printReceipt() {
    setPrinting(true); setErr(null);
    try {
      const bytes = await buildReceiptForDeal(id);
      openForPrint(bytes, `${d?.deal_no ?? "deal"}-receipt.pdf`);
      await fileIfCommitted(
        bytes, owing <= 0 ? "paid_in_full_receipt" : "payment_receipt",
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not build the receipt.");
    } finally {
      setPrinting(false);
    }
  }

  async function print(forms: string[] | undefined, suffix: string, docType: string) {
    setPrinting(true); setErr(null);
    try {
      const bytes = await buildPacketForDeal(id, forms);
      openForPrint(bytes, `${d?.deal_no ?? "deal"}-${suffix}.pdf`);
      await fileIfCommitted(bytes, docType);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not build the packet.");
    } finally {
      setPrinting(false);
    }
  }

  /** Hand back the stored bytes. Nothing is rebuilt. */
  async function reprint(doc: ArchivedDoc) {
    setReprinting(doc.id); setErr(null);
    try {
      const bytes = await fetchArchivedDocument(doc.storage_path);
      openForPrint(bytes, filenameFor(doc, d?.deal_no ?? "deal"));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not open the filed copy.");
    } finally {
      setReprinting(null);
    }
  }

  if (err && !d) return <ErrorNote>{err}</ErrorNote>;
  if (!d) return <p className="hint">Loading</p>;

  const line = (label: string, cents: number, cls = "") => (
    <div className={`li ${cls}`} key={label}>
      <span>{label}</span><span className="money">{formatMoney(cents)}</span>
    </div>
  );
  const owing = d.total_amount_due_cents - paid;

  return (
    <>

      <div className="head">
        <div className="headline">
          <h2>{d.deal_no}</h2>
          <span className="count">{d.agreement_date}</span>
        </div>
        <div className="actions">
          <Button variant="ghost" onClick={() => startEdit("top")}
                  disabled={busy || !!edit}>
            Edit
          </Button>
          <Button onClick={() => print(undefined, "packet", "retail_packet")} disabled={printing}>
            {printing ? "Building" : "Print packet"}
          </Button>
        </div>
      </div>

      <dl className="spec dealsummary">
        <div><dt>Buyer</dt>
          <dd>{d.buyer ? `${d.buyer.first_name} ${d.buyer.last_name}` : "—"}</dd></div>
        <div><dt>Vehicle</dt>
          <dd>{d.vehicle
            ? `${d.vehicle.year} ${d.vehicle.make} ${d.vehicle.model}` : "—"}</dd></div>
        <div><dt>VIN</dt><dd className="mono">{d.vehicle?.vin ?? "—"}</dd></div>
        <div><dt>County</dt>
          <dd>{d.county_name} ({surtaxLabel(d.county_surtax_bps)})</dd></div>
        <div><dt>Status</dt><dd>{d.status}</dd></div>
        <div>
          <dt>Insurance</dt>
          <dd className={d.insurance_company ? "" : "warn"}>
            {d.insurance_company
              ? `${d.insurance_company}${d.insurance_policy_no ? ` — ${d.insurance_policy_no}` : ""}`
              : "Not recorded — the affidavit will print blank"}
          </dd>
        </div>
        <div>
          <dt>Warranty</dt>
          <dd>
            {d.warranty_days
              ? `Powertrain — ${termFor(d.warranty_days).printed}`
              : "Sold as-is"}{" "}
            <button className="linkbtn tiny" disabled={busy}
                    onClick={() => startEdit("warranty")}>
              change
            </button>
          </dd>
        </div>
        {d.void_reason && <div><dt>Void reason</dt><dd>{d.void_reason}</dd></div>}
      </dl>
      {!edit && dealSave.status === "saved" && (
        <p className="savenote"><SaveStatus save={dealSave} savedLabel="Deal changes saved" /></p>
      )}

      {edit && (
        <div className="dealform">
          <h3 className="subhead" ref={editTop}>Change this deal</h3>
          <p className="taxnote">
            Tax is not editable. It is computed by the database from the
            figures below, which is what stops the screen, the contract and
            the reports from disagreeing.
          </p>
          {vEdit && (
            <>
              <h3 className="subhead">The car</h3>
              <p className="hint dim">
                Corrected here because the title application and odometer
                statement print from this deal. The VIN, year, make and model
                are not editable — a wrong VIN means the wrong car, and is
                fixed by moving the deal, not by retyping.
              </p>
              <div className="grid2">
                <OdometerField label="Mileage" required value={vEdit.odometer}
                               onChange={(v) => setVEdit((s) => (s ? { ...s, odometer: v } : s))}
                               error={lowerMileage ? lowerMileageMsg : null} />
                <OptionSelect label="Color" value={vEdit.color} options={COLORS}
                              onChange={(v) => setVEdit((s) => (s ? { ...s, color: v } : s))} />
              </div>
              <TextField label="Trim" value={vEdit.trim}
                         onChange={(v) => setVEdit((s) => (s ? { ...s, trim: v } : s))} />
            </>
          )}

          <Select label="Buyer" value={edit.buyer_id}
                  onChange={(v) => setEdit((e) => (e ? { ...e, buyer_id: v } : e))}
                  hint="Changing this re-points the deal and everything it prints">
            {people.map((c) => (
              <option key={c.id} value={c.id}>{c.last_name}, {c.first_name}</option>
            ))}
          </Select>

          <Select label="Second buyer" value={edit.co_buyer_id ?? ""}
                  onChange={(v) => setEdit((e) => (e ? {
                    ...e,
                    co_buyer_id: v || null,
                    /* The database refuses two buyers with no answer, and one
                       buyer with an answer. Kept in step here so the save
                       cannot be refused for a reason nobody can see. */
                    joint_ownership: v ? (e.joint_ownership ?? "and") : null,
                  } : e))}
                  hint="Both names go on the title">
            <option value="">Nobody — one buyer</option>
            {people.filter((c) => c.id !== edit.buyer_id).map((c) => (
              <option key={c.id} value={c.id}>{c.last_name}, {c.first_name}</option>
            ))}
          </Select>

          {edit.co_buyer_id && (
            <Select label="How they hold it" value={edit.joint_ownership ?? "and"}
                    onChange={(v) => setEdit((e) => (e ? {
                      ...e, joint_ownership: v as "and" | "or" } : e))}
                    hint={edit.joint_ownership === "or"
                      ? "Either one can sell the car on their own"
                      : "Both must sign to sell the car"}>
              <option value="and">AND — both must sign to sell</option>
              <option value="or">OR — either can sell alone</option>
            </Select>
          )}
          <div className="grid2">
            <PastDateField label="Agreement date" required value={edit.agreement_date}
                   onChange={(v) => setEdit((e) => (e ? { ...e, agreement_date: v } : e))} />
            <Select label="County" value={edit.county_name}
                    onChange={(v) => setEdit((e) => e ? {
                      ...e, county_name: v,
                      county_surtax_bps:
                        counties.find((c) => c.name === v)?.surtax_bps ?? 0,
                    } : e)}>
              {counties.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name} ({surtaxLabel(c.surtax_bps)})
                </option>
              ))}
            </Select>
          </div>
          <div className="grid2">
            <MoneyField label="Cash price" value={formatMoney(edit.cash_price_cents)}
                        onChange={editMoney("cash_price_cents")} />
            <MoneyField label="Dealer services"
                        value={formatMoney(edit.predelivery_service_cents)}
                        onChange={editMoney("predelivery_service_cents")} />
          </div>
          <div className="grid2">
            <MoneyField label="Electronic filing"
                        value={formatMoney(edit.electronic_filing_cents)}
                        onChange={editMoney("electronic_filing_cents")} />
            <MoneyField label="License and registration"
                        value={formatMoney(edit.license_registration_cents)}
                        onChange={editMoney("license_registration_cents")} />
          </div>
          <div className="grid2">
            <MoneyField label="Title fee" value={formatMoney(edit.title_fee_cents)}
                        onChange={editMoney("title_fee_cents")} />
            <MoneyField label="Temp tag" value={formatMoney(edit.temp_tag_cents)}
                        onChange={editMoney("temp_tag_cents")} />
          </div>
          <div className="grid2">
            <MoneyField label="Trade allowance"
                        value={formatMoney(edit.trade_allowance_cents)}
                        onChange={editMoney("trade_allowance_cents")} />
            <MoneyField label="Trade payoff" value={formatMoney(edit.trade_payoff_cents)}
                        onChange={editMoney("trade_payoff_cents")} />
          </div>
          <div className="grid2">
            <MoneyField label="Cash down agreed" value={formatMoney(edit.cash_down_cents)}
                        onChange={editMoney("cash_down_cents")} />
            <MoneyField label="Deferred down agreed"
                        value={formatMoney(edit.deferred_down_cents)}
                        onChange={editMoney("deferred_down_cents")} />
          </div>
          <div ref={warrantyField}>
            <Select label="In-house warranty" value={String(edit.warranty_days)}
                    onChange={(v) => setEdit((e) => (e ? {
                      ...e,
                      warranty_days: Number(v),
                      /* Set together, always. The guide prints the months and
                         the miles from these two, and a deal carrying 90 days
                         against 6,000 miles is a contract nobody can honour. */
                      warranty_miles: termFor(Number(v)).miles,
                    } : e))}
                    hint={edit.warranty_days
                      ? `Powertrain, ${termFor(edit.warranty_days).printed}. `
                        + "The Buyers Guide will say so."
                      : "The Buyers Guide will show AS IS — no dealer warranty."}>
              {WARRANTY_TERMS.map((t) => (
                <option key={t.days} value={t.days}>{t.label}</option>
              ))}
            </Select>
            {docs.length > 0 && (
              <p className="taxnote">
                A packet has already been filed against this deal. Changing the
                warranty changes which Buyers Guide prints, and the copy
                already filed stays as it was — reprint and have it signed
                again if the customer has the old one.
              </p>
            )}
          </div>

          <div className="grid2" ref={insuranceFields}>
            <TextField label="Insurance company" value={edit.insurance_company ?? ""}
                   onChange={(v) => setEdit((e) => (e ? { ...e, insurance_company: v || null } : e))} />
            <TextField label="Policy number" value={edit.insurance_policy_no ?? ""}
                   onChange={(v) => setEdit((e) => (e ? { ...e, insurance_policy_no: v || null } : e))} />
          </div>
          <InsuranceCodeField label="Insurance company code"
                              value={edit.insurance_company_code ?? ""}
                              onChange={(v) => setEdit((e) => (e ? { ...e, insurance_company_code: v || null } : e))} />
          <SaveBar save={dealSave} onSave={() => void commitEdit()}
                   label="Save changes" idle="No changes yet">
            <Button variant="ghost" onClick={cancelEdit} disabled={dealSave.saving}>
              Cancel
            </Button>
          </SaveBar>
        </div>
      )}

      <div className="dealgrid">
      {d.buyer && (
        <Panel title="Buyer and driver" rail="record"
               foot={<Button variant="ghost"
                             onClick={() => onOpenCustomer(d.buyer!.id)}>
                       Edit customer
                     </Button>}>
          <dl className="spec">
            <div><dt>Name</dt><dd>{[d.buyer.first_name, d.buyer.middle_name,
              d.buyer.last_name, d.buyer.suffix].filter(Boolean).join(" ")}</dd></div>
            <div><dt>Date of birth</dt><dd>{d.buyer.date_of_birth}</dd></div>
            <div><dt>{ID_LABEL[d.buyer.id_type] ?? "ID"}</dt>
              <dd className="mono">{d.buyer.id_number} ({d.buyer.id_state})</dd></div>
            {d.buyer.id_expiration && (
              <div><dt>Expires</dt><dd>{d.buyer.id_expiration}</dd></div>
            )}
            <div><dt>Address</dt><dd>{d.buyer.street}, {d.buyer.city} {d.buyer.state} {d.buyer.zip}</dd></div>
            {d.buyer.phone_cell && <div><dt>Phone</dt><dd>{d.buyer.phone_cell}</dd></div>}
            {d.buyer.email && <div><dt>Email</dt><dd>{d.buyer.email}</dd></div>}
          </dl>
        </Panel>
      )}

      <Panel title="Insurance" rail="record"
             note={d.insurance_company ? undefined : "not recorded"}
             foot={<Button variant="ghost" onClick={() => startEdit("insurance")}
                           disabled={busy}>
                     {edit ? "Go to the insurance fields" : "Edit insurance"}
                   </Button>}>
        {d.insurance_company ? (
          <dl className="spec">
            <div><dt>Company</dt><dd>{d.insurance_company}</dd></div>
            <div><dt>Policy</dt><dd className="mono">{d.insurance_policy_no ?? "—"}</dd></div>
            {d.insurance_company_code && (
              <div><dt>Company code</dt><dd className="mono">{d.insurance_company_code}</dd></div>
            )}
          </dl>
        ) : (
          <p className="hint">
            Nothing recorded. The Florida insurance affidavit prints from
            these, so add them with Edit before printing the packet.
          </p>
        )}
      </Panel>

      </div>

      <div className="dealgrid">
      <Panel title="Itemization" rail="money"
             figure={formatMoney(d.total_amount_due_cents)}>
      <div className="item">
        {line("Cash price", d.cash_price_cents)}
        {d.predelivery_service_cents > 0 && line("Dealer services", d.predelivery_service_cents)}
        {d.electronic_filing_cents > 0 && line("Electronic filing", d.electronic_filing_cents)}
        {d.trade_allowance_cents > 0 && line("Less trade allowance", -d.trade_allowance_cents, "neg")}
        {line("Taxable amount", d.taxable_amount_cents, "total")}
        {line("Sales tax (6%)", d.state_tax_cents)}
        {line(`Surtax (${bpsToPercent(d.county_surtax_bps)})`, d.surtax_cents)}
        {line("Total sales price", d.total_sales_price_cents, "total")}
        {d.license_registration_cents > 0 && line("License and registration", d.license_registration_cents)}
        {d.title_fee_cents > 0 && line("Title fee", d.title_fee_cents)}
        {d.temp_tag_cents > 0 && line("Temp tag", d.temp_tag_cents)}
        {d.trade_payoff_cents > 0 && line("Plus trade payoff", d.trade_payoff_cents)}
        {line("Total due", d.total_amount_due_cents, "total")}
        {d.cash_down_cents > 0 && line("Cash down agreed", -d.cash_down_cents, "neg")}
        {d.deferred_down_cents > 0 && line("Deferred down agreed", -d.deferred_down_cents, "neg")}
      </div>
      </Panel>

      <Panel title="Payments received"
             rail={owing > 0 ? "work" : "keep"}
             figure={formatMoney(paid)}>
      {payments.length === 0 ? (
        <p className="hint">Nothing collected yet.</p>
      ) : (
        <div className="item">
          {payments.map((p) => (
            <div className={`li ${p.reversed_at ? "neg" : ""}`} key={p.id}>
              <span>
                {p.paid_on} — {describeCard(p) ?? METHOD_LABEL[p.method] ?? p.method}
                {!p.card_last4 && p.reference_no ? ` (${p.reference_no})` : ""}
                {p.stripe_payment_intent_id && (
                  <>
                    <br />
                    <span className="dim confirmid">
                      {p.stripe_payment_intent_id}
                      <button className="linkbtn tiny"
                              onClick={() => navigator.clipboard
                                ?.writeText(p.stripe_payment_intent_id!)
                                .then(() => setCopied(p.id))
                                .catch(() => {})}>
                        {copied === p.id ? "copied" : "copy"}
                      </button>
                    </span>
                  </>
                )}
                {p.reversed_at ? (
                  <>
                    {" "}<em>reversed{p.reversed_reason ? `: ${p.reversed_reason}` : ""}</em>
                    {p.method === "card" && p.stripe_payment_intent_id
                      && !p.stripe_refund_id && (
                      <>
                        {" "}<em className="owed">not refunded</em>
                        <button className="linkbtn tiny" disabled={busy}
                                onClick={() => refundOutstanding(p)}>
                          refund now
                        </button>
                      </>
                    )}
                    <button className="linkbtn tiny" disabled={busy}
                            onClick={() => undoReversal(p.id)}>
                      undo
                    </button>
                  </>
                ) : (
                  <>
                    <button className="linkbtn tiny" disabled={busy}
                            onClick={() => {
                              const start = {
                                id: p.id, paid_on: p.paid_on,
                                amount: formatMoney(p.amount_cents),
                                method: p.method, ref: p.reference_no ?? "",
                              };
                              editPayStart.current = JSON.stringify(start);
                              payEditSave.reset();
                              setEditPay(start);
                            }}>
                      edit
                    </button>
                    <button className="linkbtn tiny" disabled={busy}
                            onClick={() => { setReversing(p.id); setReverseReason(""); }}>
                      reverse
                    </button>
                    <button className="linkbtn tiny" disabled={busy}
                            onClick={() => setConfirmPayment(p)}>
                      remove
                    </button>
                  </>
                )}
              </span>
              <span className="money">{formatMoney(p.amount_cents)}</span>
            </div>
          ))}
          {line("Collected", paid, "total")}
          {line(owing > 0 ? "Still owing" : "Fully paid", Math.abs(owing), "due")}
        </div>
      )}
      </Panel>

      </div>

      {balanceIsOwed(d.status) && owing > 0 && cardPaymentsConfigured() && (
        <Panel title="Take a card" rail="keep"
               note={`up to ${formatMoney(Math.min(CARD_MAX_CENTS, owing))}`}
               foot={!takingCard && (
                 <Button variant="ghost" onClick={() => setTakingCard(true)}>
                   Take a card payment
                 </Button>
               )}>
          {takingCard ? (
            <CardPayment
              dealId={id}
              owingCents={owing}
              onTaken={() => { setTakingCard(false); load(); }}
              onCancel={() => setTakingCard(false)}
            />
          ) : (
            <p className="hint">
              For a deposit or a down payment. Cards are capped at{" "}
              {formatMoney(CARD_MAX_CENTS)} — the balance goes on a cashier's
              check, ACH or wire.
            </p>
          )}
        </Panel>
      )}

      {confirmPayment && (
        <Confirm
          message={
            `Remove the ${formatMoney(confirmPayment.amount_cents)} `
            + `${(METHOD_LABEL[confirmPayment.method] ?? confirmPayment.method).toLowerCase()} `
            + `payment from ${confirmPayment.paid_on}? It disappears from the record `
            + "entirely. To show that it did not clear, reverse it instead."
          }
          confirmLabel={busy ? "Removing" : "Remove payment"}
          onConfirm={() => removePayment(confirmPayment.id)}
          onCancel={() => setConfirmPayment(null)}
        />
      )}

      {editPay && (
        <>
          <h3 className="subhead">Change this payment</h3>
          <div className="grid2">
            <PastDateField label="Date paid" required value={editPay.paid_on}
                   onChange={(v) => setEditPay((e) => (e ? { ...e, paid_on: v } : e))} />
            <MoneyField label="Amount" value={editPay.amount}
                        onChange={(v) => setEditPay((e) => (e ? { ...e, amount: v } : e))} />
          </div>
          <div className="grid2">
            <Select label="Method" value={editPay.method}
                    onChange={(v) => setEditPay((e) => (e ? { ...e, method: v } : e))}>
              {METHODS.map((m) => <option key={m} value={m}>{METHOD_LABEL[m]}</option>)}
            </Select>
            <TextField label="Reference" value={editPay.ref}
                   onChange={(v) => setEditPay((e) => (e ? { ...e, ref: v } : e))} />
          </div>
          <SaveBar save={payEditSave} onSave={() => void commitPaymentEdit()}
                   label="Save payment" idle="No changes yet">
            <Button variant="ghost" onClick={cancelPaymentEdit} disabled={payEditSave.saving}>
              Cancel
            </Button>
          </SaveBar>
        </>
      )}

      {reversing && (
        <>
          <h3 className="subhead">Reverse this payment</h3>
          <p className="taxnote">
            {payments.find((x) => x.id === reversing)?.method === "card"
              ? "This refunds the full amount to the card through Stripe, then "
                + "marks the payment reversed. The money leaves your Stripe "
                + "balance. It cannot be undone from here."
              : "The payment stays on the record and stops counting toward the "
                + "balance. Use this for a check that bounced — not for a typing "
                + "mistake, which is what edit is for."}
          </p>
          <TextField label="Why" required value={reverseReason} onChange={setReverseReason}
                 hint="Kept with the payment" />
          <div className="actions">
            <Button variant="danger" onClick={commitReversal} disabled={busy}>
              {payAct.saving ? "Reversing…" : "Reverse payment"}
            </Button>
            <Button variant="ghost" onClick={() => setReversing(null)} disabled={busy}>
              Cancel
            </Button>
          </div>
        </>
      )}

      <p className="savenote">
        <SaveStatus save={payAct} hideWhenIdle savedLabel={payActLabel} />
      </p>
      <SaveError save={payAct}
                 hint="Reload the deal to see where the payment stands before trying again." />

      <div className="grid2" style={{ marginTop: 14 }}>
        <MoneyField label="Amount" value={pay.amount}
                    onChange={(v) => setPay((s) => ({ ...s, amount: v }))} />
        <Select label="Method" value={pay.method}
                onChange={(v) => setPay((s) => ({ ...s, method: v }))}>
          {METHODS.map((m) => <option key={m} value={m}>{METHOD_LABEL[m]}</option>)}
        </Select>
      </div>
      <TextField label="Reference" value={pay.ref}
             onChange={(v) => setPay((s) => ({ ...s, ref: v }))}
             hint="Check or confirmation number" />
      <SaveBar save={paySave} onSave={() => void addPayment()} label="Record payment"
               savingLabel="Recording…" savedLabel="Payment recorded"
               retryHint="The amount is still filled in — press Record payment to try again." />

      {/* Money has come in and money is still owed — the state that gets
          forgotten. Quotes are excluded: nothing is owed on a proposal. */}
      {paid > 0 && owing > 0 && balanceIsOwed(d.status) && (
        <BalanceDue dealId={d.id} owingCents={owing}
                    customerEmail={d.buyer?.email ?? null}
                    onChanged={load} />
      )}

      {d.buyer && d.vehicle && (
        <WeOwePanel
          dealId={d.id}
          dealNo={d.deal_no}
          dealStatus={d.status}
          vehicle={`${d.vehicle.year} ${d.vehicle.make} ${d.vehicle.model}`}
          vin={d.vehicle.vin}
          buyer={`${d.buyer.first_name} ${d.buyer.last_name}`}
        />
      )}

      {d.buyer && d.vehicle && (signing || resuming ? (
        <SignPacket
          dealId={d.id}
          dealNo={d.deal_no}
          docKeys={packetFor(d.warranty_days ?? 0)}
          buyerName={`${d.buyer.first_name} ${d.buyer.last_name}`}
          coBuyerName={d.co_buyer ? `${d.co_buyer.first_name} ${d.co_buyer.last_name}` : null}
          dealerName={dealerName}
          idOnFile={{
            type: d.buyer.id_type === "drivers_license" ? "Driver license"
                : d.buyer.id_type === "state_id" ? "State ID"
                : d.buyer.id_type === "passport" ? "Passport" : "Photo ID",
            state: d.buyer.id_state,
            number: d.buyer.id_number,
            expiration: d.buyer.id_expiration,
          }}
          resume={resuming}
          onClose={() => { setSigning(false); setResuming(null); }}
          onSealed={() => { setSigning(false); setResuming(null); load(); }}
        />
      ) : (
        <SignChoice
          dealId={d.id}
          dealNo={d.deal_no}
          docKeys={packetFor(d.warranty_days ?? 0)}
          buyerName={`${d.buyer.first_name} ${d.buyer.last_name}`}
          coBuyerName={d.co_buyer ? `${d.co_buyer.first_name} ${d.co_buyer.last_name}` : null}
          buyerEmail={d.buyer.email}
          vehicle={`${d.vehicle.year} ${d.vehicle.make} ${d.vehicle.model}`}
          dealerName={dealerName}
          onInPerson={() => setSigning(true)}
          onFinish={(session) => setResuming(session)}
        />
      ))}

      <div className="dealgrid">
      <Panel title="Paperwork" rail="record">
      <div className="packet">
        {packetFor(d.warranty_days ?? 0).map((k) => (
          <div className="doc" key={k}>
            <span className="tick">✓</span>{PACKET_LABELS[k]}
          </div>
        ))}
      </div>
      <p className="taxnote">
        Five documents, one print job. Have Florida counsel review the purchase
        agreement before it goes in front of a customer.
      </p>

      <div className="actions">
        <Button variant="ghost" disabled={printing}
                onClick={() => print(DEPOSIT_PACKET, "deposit-receipt", "deposit_receipt")}>
          Deposit receipt
        </Button>
        <Button variant="ghost" disabled={printing} onClick={printReceipt}>
          {owing <= 0 ? "Paid-in-full receipt" : "Payment receipt"}
        </Button>
      </div>
      <p className="taxnote">
        The receipt prints with the dealer, customer and vehicle filled in. The
        amount, hold period and refundability are left blank on purpose —
        § 501.976(10) requires those to be agreed and written down, not
        defaulted by software.
      </p>

      </Panel>

      <Panel title="Filed copies" rail="record"
             note={docs.length ? `${docs.length} on file` : undefined}>
      {docs.length === 0 ? (
        <p className="hint">
          {balanceIsOwed(d.status)
            ? "Nothing filed yet. Anything printed from here is kept."
            : "Nothing is filed until this deal is sold. Print freely until then."}
        </p>
      ) : (
        <div className="item">
          {docs.map((doc) => (
            <div className="li" key={doc.id}>
              <span>
                {doc.generated_at.slice(0, 10)} — {labelForDocType(doc.doc_type)}
                <button className="linkbtn tiny" disabled={reprinting !== null}
                        onClick={() => reprint(doc)}>
                  {reprinting === doc.id ? "opening" : "reprint"}
                </button>
              </span>
              <span className="money">{formatSize(doc.byte_size)}</span>
            </div>
          ))}
        </div>
      )}
      {archiveNote && <ErrorNote>{archiveNote}</ErrorNote>}
      <p className="taxnote">
        A reprint hands back the file as it was produced. It is never rebuilt,
        so a packet printed today still prints the same way after a form
        template or a fee has changed. Kept 10 years.
      </p>

      </Panel>

      </div>

      {d.buyer && d.vehicle && (
        <EmailPacket
          dealId={d.id}
          dealNo={d.deal_no}
          buyerName={`${d.buyer.first_name} ${d.buyer.last_name}`}
          buyerEmail={d.buyer.email}
          vehicle={`${d.vehicle.year} ${d.vehicle.make} ${d.vehicle.model}`}
          vin={d.vehicle.vin}
          dealerName={dealerName}
          filedCount={docs.length}
        />
      )}

      <h3 className="subhead">Move it along</h3>
      <div className="statusrow">
        {["quote", "deposit", "sold"].map((s) => (
          <button key={s} className={`chip ${d.status === s ? "on" : ""}`}
                  disabled={busy || d.status === s}
                  onClick={() => setStatus(s)}>
            {s[0]!.toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {err && <ErrorNote>{err}</ErrorNote>}
      <p className="savenote">
        <SaveStatus save={dealAct} hideWhenIdle savedLabel={dealActLabel} />
      </p>
      <SaveError save={dealAct} hint="Reload the deal to see where it stands." />

      {(() => {
        const blocked = deleteBlockedReason(d.status, payments.length, docs.length);
        return (
          <>
            <h3 className="subhead">Delete</h3>
            {blocked ? (
              <p className="taxnote">{blocked}</p>
            ) : deleting ? (
              /* Asking for the deal number typed back was a developer's idea
                 of care. There is nothing here to lose but a proposal, and
                 the check for payments and filed paperwork above is what
                 actually protects the records. */
              <Confirm
                message={`Delete ${d.deal_no} for good? This cannot be undone.`}
                confirmLabel={busy ? "Deleting" : "Delete deal"}
                onConfirm={commitDelete}
                onCancel={() => setDeleting(false)}
              />
            ) : (
              <div className="actions">
                <Button variant="danger" onClick={() => setDeleting(true)}>
                  Delete deal
                </Button>
              </div>
            )}
          </>
        );
      })()}

      {d.status === "voided" && (
        <p className="taxnote">
          This deal is voided and the car is back on the lot. Choosing a status
          above puts it back in play. It can also be deleted, since a voided
          deal is not a sale anybody has to keep.
        </p>
      )}

      {d.status !== "voided" && (
        voiding ? (
          <Confirm
            message="Voiding returns the car to the lot and keeps this deal on record."
            confirmLabel={busy ? "Voiding" : "Void deal"}
            onConfirm={doVoid}
            onCancel={() => setVoiding(false)}
          />
        ) : (
          <div className="actions">
            <Button variant="danger" onClick={() => setVoiding(true)}>Void deal</Button>
          </div>
        )
      )}
      {voiding && (
        <TextField label="Reason" required value={voidReason} onChange={setVoidReason}
               hint="Kept with the record" />
      )}
    </>
  );
}
